import { useRef, useState } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
  AccordionTrigger,
} from "../../elements/accordion/accordion";
import { ChoiceInput } from "../../elements/choice-input/choice-input";
import { Label } from "../../elements/label/label";
import { RadioGroup } from "../../elements/radio-group/radio-group";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";

export type FilterOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type AccordionFilterProps = {
  title: string;
  options: FilterOption[];
  variant?: "checkbox" | "radio";
  // Controlled API: selected values (checkbox = many, radio = single as array)
  selectedValues: string[];
  defaultSelectedValues: string[];
  resetSelectedValues?: string[];
  onSelectionChange?: (values: string[]) => void;
  onReset?: () => void;
  resetLabel: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  name?: string;
};

//using useLatest pattern to avoid using useEffect
function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function areValuesEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const rightValues = new Set(right);

  return left.every((value) => rightValues.has(value));
}

export function AccordionFilter(props: Readonly<AccordionFilterProps>) {
  const latestSelectedValuesRef = useLatest(props.selectedValues);
  const isOpenByDefault =
    props.defaultOpen ?? latestSelectedValuesRef.current.length > 0;
  const [internalIsOpen, setInternalIsOpen] = useState(isOpenByDefault);
  const isOpen = props.open ?? internalIsOpen;

  const resetSelectedValues =
    props.resetSelectedValues ?? props.defaultSelectedValues;

  function setOpen(open: boolean) {
    if (props.open === undefined) {
      setInternalIsOpen(open);
    }

    props.onOpenChange?.(open);
  }

  function updateSelection(next: string[]) {
    props.onSelectionChange?.(next);
  }

  function toggleCheckbox(value: string) {
    const exists = latestSelectedValuesRef.current.includes(value);
    const next = exists
      ? latestSelectedValuesRef.current.filter((v) => v !== value)
      : [...latestSelectedValuesRef.current, value];
    updateSelection(next);
  }

  function handleResetFromTrigger() {
    props.onReset?.();
    setOpen(false);
  }

  const inputName =
    props.name ||
    `accordion-filter-${props.title.replace(/\s+/g, "-").toLowerCase()}`;
  const resetCountSuffix =
    latestSelectedValuesRef.current.length > 0
      ? ` (${latestSelectedValuesRef.current.length})`
      : "";
  const resetLabel =
    props.variant === "radio"
      ? props.resetLabel
      : props.resetLabel + resetCountSuffix;
  const isResetDisabled = areValuesEqual(
    latestSelectedValuesRef.current,
    resetSelectedValues
  );

  return (
    <div className={cn("accordion-filter", props.className)}>
      <AccordionPrimitive
        collapsible
        onValueChange={(value) => setOpen(value === "open")}
        type="single"
        value={isOpen ? "open" : ""}
      >
        <AccordionItem value="open">
          <AccordionTrigger
            className="pt-4 pb-2"
            onReset={() => handleResetFromTrigger()}
            resetDisabled={isResetDisabled}
            resetId={inputName}
            resetLabel={resetLabel}
            triggerVariant="reset"
          >
            <div className="flex w-full items-center justify-between">
              <Text variant="body-sm-bold">{props.title}</Text>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-1.5 py-1 [&_label]:items-center">
              {props.variant === "radio" ? (
                <RadioGroup
                  labelVariant="body-sm"
                  name={inputName}
                  onValueChange={(v) => updateSelection(v ? [v] : [])}
                  options={props.options}
                  value={latestSelectedValuesRef.current[0]}
                />
              ) : (
                props.options.map((opt) => {
                  const id = `${inputName}-${opt.value}`;
                  const checked = latestSelectedValuesRef.current.includes(
                    opt.value
                  );
                  return (
                    <Label
                      className="flex cursor-pointer select-none items-start gap-2"
                      disabled={opt.disabled}
                      htmlFor={id}
                      key={opt.value}
                      variant="default"
                    >
                      <ChoiceInput
                        checked={checked}
                        className="mt-px"
                        disabled={opt.disabled}
                        id={id}
                        name={inputName}
                        onChange={() => {
                          if (opt.disabled) {
                            return;
                          }
                          toggleCheckbox(opt.value);
                        }}
                        type="checkbox"
                      />

                      <div className="flex flex-col">
                        <Text variant="body-sm">{opt.label}</Text>
                        {opt.description && (
                          <Text color="muted" variant="body-xs">
                            {opt.description}
                          </Text>
                        )}
                      </div>
                    </Label>
                  );
                })
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </AccordionPrimitive>
    </div>
  );
}
